import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { snackbar, FullScreenProgress } from '@occc-app/design-system';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';

import { RetailerNetworkMyStoresGroupV2Page } from './retailer-network-my-stores-group-v2-page.component';
import {
  ALL_GROUP_LOCATIONS_PAGE_SIZE,
  TABLE_PAGE_SIZE,
} from './retailer-network-my-stores-group-v2-page.utils';

import { RetailerNetworkNoData } from '../components/retailer-network-no-data.component';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { routeCreator } from '../retailer-connect.utils';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';
import {
  useGetRetailerNetworkLocationGroups,
  useGetRetailerNetworkLocations,
} from '../../data-access/queries';
import {
  useRetailerNetworkLocationGroupAddLocationsMutation,
  useRetailerNetworkLocationGroupRemoveLocationsMutation,
} from '../../data-access/mutations';
import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';

const ADD_REMOVE_REFETCH_QUERIES = ['retailerNetworkLocations', 'retailerNetworkLocationGroups'];

export const RetailerNetworkMyStoresGroupV2PageContainer = () => {
  const { networkId = '', locationGroupId = '' } = useParams<{
    networkId: string;
    locationGroupId: string;
  }>();
  const { isRetailerNetworkSiteUserRole, retailerNetworkUserUuid, retailerNetworkUserOpenCommerceUserUuid } =
    useRetailerNetworkUserSelector();

  // Site roles see only their assigned locations within the group.
  const userScopingFilter =
    isRetailerNetworkSiteUserRole && retailerNetworkUserUuid
      ? {
          retailerNetworkUserUuidEquals: retailerNetworkUserUuid,
          openCommerceUserUuidEquals: retailerNetworkUserOpenCommerceUserUuid ?? undefined,
        }
      : {};

  const [tableSearch, setTableSearch] = useSearchParams();
  const [tablePaginationInfo, onTablePageChange] = usePaginationParams({
    pageSize: TABLE_PAGE_SIZE,
  });

  const {
    data: locationGroupData,
    loading: isLocationGroupLoading,
    error: locationGroupError,
  } = useGetRetailerNetworkLocationGroups({
    filter: {
      retailerNetworkUuidEquals: networkId,
      ...userScopingFilter,
      retailerNetworkLocationGroupUuidEquals: locationGroupId,
    },
    pageSize: 1,
  });

  // All locations fetched at once so every group pin appears on the map regardless of table page.
  const { data: allLocations = [], loading: isAllLocationsLoading } =
    useGetRetailerNetworkLocations({
      filter: {
        retailerNetworkUuidEquals: networkId,
        ...userScopingFilter,
        retailerNetworkLocationGroupUuidContains: [locationGroupId],
      },
      pageSize: ALL_GROUP_LOCATIONS_PAGE_SIZE,
    });

  // Separate paginated + searchable query for the table.
  const {
    data: tableLocations = [],
    loading: areTableLocationsLoading,
    error: tableLocationsError,
    paginationProps: tablePaginationProps,
  } = useGetRetailerNetworkLocations({
    filter: {
      retailerNetworkUuidEquals: networkId,
      ...userScopingFilter,
      retailerNetworkLocationGroupUuidContains: [locationGroupId],
      search: tableSearch,
    },
    pageSize: TABLE_PAGE_SIZE,
    paginationInfo: tablePaginationInfo,
    onPageChange: onTablePageChange,
  });

  const [addLocationsMutation, { loading: isAddLocationToLocationGroupInProgress }] =
    useRetailerNetworkLocationGroupAddLocationsMutation({
      onCompleted: () => snackbar.success('Location(s) successfully added to the Location Group'),
      onError: () => snackbar.error('Error adding Location(s) to the Location Group'),
      refetchQueries: ADD_REMOVE_REFETCH_QUERIES,
      awaitRefetchQueries: true,
    });

  const addLocationsToLocationGroup = async (locationUuids: string[]) => {
    await addLocationsMutation({
      variables: {
        retailerNetworkUuid: networkId,
        locationGroupUuid: locationGroupId,
        locationUuids,
      },
    });
  };

  const [removeLocationMutation, { loading: isRemoveLocationFromGroupInProgress }] =
    useRetailerNetworkLocationGroupRemoveLocationsMutation({
      onCompleted: () =>
        snackbar.success('Location(s) successfully removed from the Location Group'),
      onError: () => snackbar.error('Error removing Location(s) from the Location Group'),
      refetchQueries: ADD_REMOVE_REFETCH_QUERIES,
      awaitRefetchQueries: true,
    });

  const removeLocationFromLocationGroup = async (locationUuid: string) => {
    await removeLocationMutation({
      variables: {
        retailerNetworkUuid: networkId,
        locationGroupUuid: locationGroupId,
        locationUuids: [locationUuid],
      },
    });
  };

  const locationGroup = locationGroupData?.[0];

  const breadcrumbLinks = useMemo(
    () => [
      { label: 'My Stores', to: routeCreator.myStores(networkId, true) },
      { label: locationGroup?.name || '' },
    ],
    [locationGroup?.name, networkId],
  );
  useBreadcrumbLinks(breadcrumbLinks);

  if (isLocationGroupLoading && !locationGroup) {
    return <FullScreenProgress />;
  }

  if (locationGroupError) {
    return <RetailerNetworkErrorMessage />;
  }

  if (!locationGroup) {
    return <RetailerNetworkNoData />;
  }

  return (
    <RetailerNetworkMyStoresGroupV2Page
      locationGroup={locationGroup}
      allLocations={allLocations}
      isAllLocationsLoading={isAllLocationsLoading}
      tableLocations={tableLocations}
      areTableLocationsLoading={areTableLocationsLoading}
      tableLocationsError={tableLocationsError}
      tablePaginationProps={tablePaginationProps}
      tableSearch={tableSearch}
      onTableSearch={setTableSearch}
      addLocationsToLocationGroup={addLocationsToLocationGroup}
      removeLocationFromLocationGroup={removeLocationFromLocationGroup}
      isAddLocationToLocationGroupInProgress={isAddLocationToLocationGroupInProgress}
      isRemoveLocationFromGroupInProgress={isRemoveLocationFromGroupInProgress}
    />
  );
};
